import { Box, Button, Flex, Link, Text } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import ModalDrawer from "@components/ModalDrawer";
import { formatEtherNumber, LSTIconMapping } from "@constants/common";
import { Airdrop, ClaimData, ClaimType } from "@hooks/useClaims";
import { trackEvent } from "@lib/utils";
import { CLAIMS_TABS_CHANGED, VIEW_STAKED_KERNEL } from "@constants/analytics";
import {
  Card,
  Button as KButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Typography,
} from "@kelp-dao/kernel-design-system";
import { InfoIcon, XCircleIcon } from "@phosphor-icons/react";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { formatDate, isKernelAirdropClaim } from "@common/utils";
import { useMemo, useState } from "react";
import { useAccount, useSwitchChain } from "wagmi";
import GainNudge from "./GainNudge";
import TableSkeleton from "./TableSkeleton";
import Disconnected from "@components/Dashboard-v2/Disconnected";

const tabs = [
  { label: "Eligible airdrops", value: "eligible" },
  { label: "Ineligible airdrops", value: "ineligible" },
  { label: "Future airdrops", value: "future" },
  { label: "Claimed airdrops", value: "claimed" },
];

type ActiveAirDropsProps = {
  airdrops: ClaimData[];
  handleClaim: (data: Airdrop) => void;
  isLoading: boolean;
  hasSecretKeyForClaim?: boolean;
  futureAirdrops?: {
    id: number;
    asset_name: string;
    icon_path: string | null;
    release_date: string;
  }[];
  claimedAirdrops?: ClaimData[];
};

const ActiveAirDrops = ({
  airdrops = [],
  handleClaim,
  isLoading,
  hasSecretKeyForClaim = false,
  futureAirdrops = [],
  claimedAirdrops = [],
}: ActiveAirDropsProps) => {
  const [activeTab, setActiveTab] = useState<string>("eligible");
  const [futureExpanded, setFutureExpanded] = useState(false);
  const [claimedExpanded, setClaimedExpanded] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const { isConnected, chainId } = useAccount();
  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const handleConnectWallet = () => {
    openConnectModal?.();
  };

  const handleSwitchNetwork = (chainId: number) => () => {
    switchChain({ chainId });
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    trackEvent(CLAIMS_TABS_CHANGED, {
      chain_Id: String(chainId || ""),
      tab_selected: tab,
    });
  };

  const [eligibleAirdrops, ineligibleAirdrops] = useMemo(() => {
    const eligibleAirdrops = airdrops.filter(
      (airdrop) =>
        Number(airdrop.data?.availableAmount) > 0 &&
        airdrop.data?.proofs?.length > 0
    );

    const ineligibleAirdrops = airdrops.filter(
      (airdrop) =>
        airdrop.key !== ClaimType.KERNEL_S3V1 &&
        airdrop.key !== ClaimType.KERNEL_S3V1_BSC &&
        !(Number(airdrop.data?.availableAmount) > 0) &&
        !(Number(airdrop.data?.claimedAmount) > 0)
    );

    return [eligibleAirdrops, ineligibleAirdrops];
  }, [airdrops]);

  const renderTooltip = (text: string, id?: string) => {
    if (text) {
      const tooltipId = id || text; // Use provided id or fallback to text
      const isTooltipOpen = activeTooltip === tooltipId;

      return (
        <>
          <Tooltip>
            <TooltipTrigger asChild className="hidden lg:block">
              <InfoIcon className="cursor-pointer" />
            </TooltipTrigger>
            <TooltipContent
              className="z-50 overflow-hidden rounded-lg bg-bg-primary px-3 py-2 text-sm text-text-secondary shadow-lg max-w-xs break-words"
              sideOffset={8}
            >
              {text}
            </TooltipContent>
          </Tooltip>
          <div className="block lg:hidden">
            <span style={{ alignSelf: "center" }}>
              <InfoIcon
                className="cursor-pointer"
                onClick={() => setActiveTooltip(tooltipId)}
              />
            </span>
            <ModalDrawer
              isOpen={isTooltipOpen}
              closeAlert={() => setActiveTooltip(null)}
              contentProps={{
                p: { base: "1rem", md: "2rem" },
              }}
              onlyMobile={true}
            >
              <Flex flexDirection="column" gap="1rem" width="100%" py="4">
                <Flex justifyContent="space-between" alignItems="center">
                  <Text
                    fontSize="16px"
                    fontWeight="500"
                    mb={2}
                    color="textPrimary"
                  >
                    Information
                  </Text>
                  <Button
                    variant="unstyled"
                    position={"absolute"}
                    right={2}
                    p={0}
                    height="24px"
                    onClick={() => setActiveTooltip(null)}
                    _focus={{
                      boxShadow: "none",
                    }}
                  >
                    <XCircleIcon size={24} color="#075A5A" />
                  </Button>
                </Flex>
                <Text
                  color="textSecondary"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="1.5"
                >
                  {text}
                </Text>
              </Flex>
            </ModalDrawer>
          </div>
        </>
      );
    } else {
      return null;
    }
  };

  const shouldShowClaimInsteadOfClaimAndStake = (data: any) => {
    return hasSecretKeyForClaim && isKernelAirdropClaim(data.type);
  };

  const renderTabs = () => {
    switch (activeTab) {
      case "eligible":
        return (
          <>
            <div className="hidden lg:block">
              {renderTabPanel(eligibleAirdrops)}
            </div>
            <div className="block lg:hidden">
              {renderMobileTabPanel(eligibleAirdrops)}
            </div>
          </>
        );
      case "ineligible":
        return (
          <>
            <div className="hidden lg:block">
              {renderTabPanel(ineligibleAirdrops, "ineligible")}
            </div>
            <div className="block lg:hidden">
              {renderMobileTabPanel(ineligibleAirdrops)}
            </div>
          </>
        );
      case "future":
        return (
          <>
            <div className="hidden lg:block">
              {renderFutureAirdropsTabPanel()}
            </div>
            <div className="block lg:hidden">
              {renderFutureAirdropsMobilePanel()}
            </div>
          </>
        );
      case "claimed":
        return (
          <>
            <div className="hidden lg:block">
              {renderClaimedAirdropsTabPanel()}
            </div>
            <div className="block lg:hidden">
              {renderClaimedAirdropsMobilePanel()}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderTabPanel = (airdrops: ClaimData[], _type = "eligible") => {
    if (isLoading) {
      return <TableSkeleton rows={3} columns={3} />;
    }

    if (airdrops.length === 0) {
      return (
        <Box padding="1rem 1rem 1rem 0.5rem">
          <Text>No airdrops available.</Text>
        </Box>
      );
    }

    return (
      <Box>
        <Table>
          <TableHeader>
            <TableRow style={{ borderBottom: "none" }}>
              <TableHead className="pl-1">
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Asset
                </Typography>
              </TableHead>

              <TableHead>
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Total allocation
                </Typography>
              </TableHead>

              <TableHead>
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Claimable amount
                </Typography>
              </TableHead>

              {_type === "eligible" && (
                <TableHead>
                  <Typography
                    type="body"
                    variant="small"
                    className="text-text-secondary"
                  >
                    Vesting end date
                  </Typography>
                </TableHead>
              )}

              <TableHead>
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Claim End Date
                </Typography>
              </TableHead>

              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {airdrops.map((airdrop: ClaimData, index: number) => {
              const { data, isRefetching } = airdrop;

              return (
                <TableRow key={index}>
                  <TableCell className="pl-1">
                    <Flex gap="0.4rem" alignItems="center">
                      <BaseImage
                        src={airdrop.data?.iconPath}
                        height={20}
                        width={20}
                        alt={airdrop.key}
                      />
                      <Typography
                        type="body"
                        variant="small"
                        className="w-fit text-ellipsis overflow-hidden whitespace-nowrap"
                        title={data.asset}
                      >
                        {data.asset}
                      </Typography>
                      {isKernelAirdropClaim(data.type) && data.tooltipContent
                        ? renderTooltip(
                            data.tooltipContent,
                            `asset-tooltip-${index}`
                          )
                        : null}
                    </Flex>
                  </TableCell>
                  <TableCell>
                    <Typography type="body" variant="small">
                      {_type === "eligible"
                        ? `${formatEtherNumber(
                            data.totalAmount?.toString() || "0"
                          )} ${data.unit}`
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography type="body" variant="small">
                      {_type === "eligible"
                        ? `${formatEtherNumber(
                            data.availableAmountToClaim?.toString() ||
                              data.availableAmount.toString()
                          )} ${data.unit}`
                        : "-"}
                    </Typography>
                  </TableCell>
                  {_type === "eligible" && (
                    <TableCell>
                      <Flex gap="0.4rem" alignItems="center">
                        <Typography type="body" variant="small">
                          {data?.completeVestingDate
                            ? formatDate(data?.completeVestingDate)
                            : "-"}
                        </Typography>
                        {isKernelAirdropClaim(data.type) &&
                          data?.completeVestingDate &&
                          renderTooltip(
                            `The date by which the entire amount of tokens will be vested`,
                            `vesting-tooltip-${index}`
                          )}
                      </Flex>
                    </TableCell>
                  )}

                  <TableCell>
                    <Flex gap="0.5rem" alignItems="center">
                      <Typography type="body" variant="small">
                        {data?.claimEndDate
                          ? formatDate(data?.claimEndDate)
                          : "-"}
                      </Typography>
                      {isKernelAirdropClaim(data.type) &&
                        data?.claimEndDate &&
                        renderTooltip(
                          `Claims will be disabled after this date`,
                          `claim-end-tooltip-${index}`
                        )}
                    </Flex>
                  </TableCell>

                  <TableCell className="text-right">
                    {data.chainId !== chainId && (
                      <KButton
                        variant="outline"
                        size="xsmall"
                        onClick={handleSwitchNetwork(data.chainId)}
                        disabled={!(data.availableAmount > 0n)}
                        className={
                          !(data.availableAmount > 0n) ? "opacity-50" : ""
                        }
                      >
                        <Typography
                          type="button"
                          variant="small"
                          className="text-button-secondary-text leading-3"
                        >
                          Switch network
                        </Typography>
                      </KButton>
                    )}
                    {data.chainId === chainId && (
                      <KButton
                        onClick={() => handleClaim(data)}
                        disabled={
                          !(data.availableAmount > 0n) ||
                          isRefetching ||
                          !data.proofs ||
                          data?.proofs?.length <= 0
                        }
                        className={
                          !(data.availableAmount > 0n) ||
                          isRefetching ||
                          !data.proofs ||
                          data?.proofs?.length <= 0
                            ? "opacity-50"
                            : ""
                        }
                        variant="outline"
                        size="xsmall"
                      >
                        <Typography
                          type="button"
                          variant="small"
                          className="text-button-secondary-text leading-3"
                        >
                          {isKernelAirdropClaim(data.type) &&
                          !shouldShowClaimInsteadOfClaimAndStake(data)
                            ? "Claim Staked Kernel"
                            : "Claim"}
                        </Typography>
                      </KButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const renderMobileTabPanel = (airdrops: ClaimData[]) => {
    if (isLoading) {
      return (
        <div className="px-4 py-2">
          <TableSkeleton rows={3} columns={2} />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        {airdrops.map((airdrop, index) => {
          const { data, isRefetching } = airdrop;
          // const isEligible = type === "eligible";

          return (
            <Card
              key={index}
              variant="non-bordered"
              className="flex gap-3 p-4 rounded-xl"
            >
              <div className="flex items-start">
                <BaseImage
                  src={airdrop.data?.iconPath}
                  height={24}
                  width={24}
                  alt={airdrop.key}
                />
              </div>

              <div className="flex flex-col gap-3 w-full">
                {/* Asset name and tooltip */}
                <div className="flex items-center gap-2">
                  <Typography type="heading" variant="xxsmall">
                    {data?.asset}
                  </Typography>
                  {isKernelAirdropClaim(data.type) &&
                    data?.tooltipContent &&
                    renderTooltip(
                      data.tooltipContent,
                      `mobile-asset-tooltip-${index}`
                    )}
                </div>

                <>
                  {/* Allocation and Available Amount */}
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex flex-col gap-0.5">
                      <Typography type="body" variant="xsmall">
                        {formatEtherNumber(data.totalAmount?.toString() || "0")}{" "}
                        {data.unit}
                      </Typography>
                      <Typography
                        type="misc"
                        variant="helper"
                        className="text-text-secondary"
                      >
                        Total allocation
                      </Typography>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <Typography
                        type="body"
                        variant="xsmall"
                        className="text-right"
                      >
                        {formatEtherNumber(
                          data.availableAmountToClaim?.toString() ||
                            data.availableAmount.toString()
                        )}{" "}
                        {data.unit}
                      </Typography>
                      <Typography
                        type="misc"
                        variant="helper"
                        className="text-text-secondary text-right"
                      >
                        Available Amount
                      </Typography>
                    </div>
                  </div>

                  {/* Dates */}
                  {(data.completeVestingDate || data.claimEndDate) && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex gap-1 items-center">
                          <Typography type="body" variant="xsmall">
                            {data.completeVestingDate
                              ? formatDate(data.completeVestingDate)
                              : "-"}
                          </Typography>
                          {isKernelAirdropClaim(data.type) &&
                            data?.completeVestingDate &&
                            renderTooltip(
                              `The date by which the entire amount of tokens will be vested`,
                              `mobile-vesting-tooltip-${index}`
                            )}
                        </div>
                        <Typography
                          type="misc"
                          variant="helper"
                          className="text-text-secondary"
                        >
                          Complete vesting date
                        </Typography>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex gap-1 items-center">
                          <Typography
                            type="body"
                            variant="xsmall"
                            className="text-right"
                          >
                            {data.claimEndDate
                              ? formatDate(data.claimEndDate)
                              : "-"}
                          </Typography>
                          {isKernelAirdropClaim(data.type) &&
                            data?.claimEndDate &&
                            renderTooltip(
                              `Claims will be disabled after this date`,
                              `mobile-claim-end-tooltip-${index}`
                            )}
                        </div>
                        <Typography
                          type="misc"
                          variant="helper"
                          className="text-text-secondary text-right"
                        >
                          Claim End Date
                        </Typography>
                      </div>
                    </div>
                  )}

                  {/* Action buttons - Always show */}
                  <div className="flex justify-start gap-2">
                    {data.chainId !== chainId && (
                      <KButton
                        variant="outline"
                        size="xsmall"
                        onClick={handleSwitchNetwork(data.chainId)}
                      >
                        Switch network
                      </KButton>
                    )}
                    {data.chainId === chainId && (
                      <KButton
                        variant="outline"
                        size="xsmall"
                        onClick={() => handleClaim(data)}
                        disabled={
                          !(data.availableAmount > 0n) ||
                          isRefetching ||
                          !data.proofs ||
                          data?.proofs?.length <= 0
                        }
                        className={
                          !(data.availableAmount > 0n) ||
                          isRefetching ||
                          !data.proofs ||
                          data?.proofs?.length <= 0
                            ? "opacity-50"
                            : ""
                        }
                      >
                        <Typography
                          type="button"
                          variant="small"
                          className="text-button-secondary-text leading-3"
                        >
                          {isKernelAirdropClaim(data.type) &&
                          !shouldShowClaimInsteadOfClaimAndStake(data)
                            ? "Claim Staked Kernel"
                            : "Claim"}
                        </Typography>
                      </KButton>
                    )}
                  </div>
                </>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderFutureAirdropsTabPanel = () => {
    const showViewMore = futureAirdrops.length > 3 && !futureExpanded;
    const visibleAirdrops = futureExpanded
      ? futureAirdrops
      : futureAirdrops.slice(0, 3);

    if (futureAirdrops.length === 0) {
      return (
        <Box padding="1rem 1rem 1rem 0.5rem">
          <Text>No future airdrops available.</Text>
        </Box>
      );
    }

    return (
      <Box>
        <Table>
          <TableHeader>
            <TableRow style={{ borderBottom: "none" }}>
              <TableHead className="pl-1">
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Asset
                </Typography>
              </TableHead>
              <TableHead className="text-right">
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Release Date
                </Typography>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleAirdrops.map((airdrop, index) => (
              <TableRow key={index}>
                <TableCell className="pl-1">
                  <Flex gap="0.4rem" alignItems="center">
                    {(LSTIconMapping[airdrop?.asset_name] ||
                      airdrop?.icon_path) && (
                      <BaseImage
                        src={
                          airdrop?.icon_path ||
                          LSTIconMapping[airdrop?.asset_name]
                        }
                        height={20}
                        width={20}
                        alt={airdrop?.asset_name}
                      />
                    )}
                    <Typography type="body" variant="xsmall">
                      {airdrop?.asset_name}
                    </Typography>
                  </Flex>
                </TableCell>
                <TableCell className="text-right">
                  <Typography type="body" variant="xsmall">
                    {airdrop?.release_date}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {showViewMore && (
          <div className="mt-4">
            <KButton
              variant="outline"
              size="xsmall"
              onClick={() => setFutureExpanded(true)}
            >
              <Typography
                type="button"
                variant="xsmall"
                className="text-button-secondary-text leading-3"
              >
                View More
              </Typography>
            </KButton>
          </div>
        )}
      </Box>
    );
  };

  const renderFutureAirdropsMobilePanel = () => {
    const showViewMore = futureAirdrops.length > 3 && !futureExpanded;
    const visibleAirdrops = futureExpanded
      ? futureAirdrops
      : futureAirdrops.slice(0, 3);

    if (futureAirdrops.length === 0) {
      return (
        <div className="px-4 py-2">
          <Text>No future airdrops available.</Text>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        {visibleAirdrops.map((airdrop, index) => (
          <Card
            key={index}
            variant="non-bordered"
            className="flex gap-3 p-4 rounded-xl"
          >
            <div className="flex items-start">
              {(LSTIconMapping[airdrop?.asset_name] || airdrop?.icon_path) && (
                <BaseImage
                  src={
                    airdrop?.icon_path || LSTIconMapping[airdrop?.asset_name]
                  }
                  height={24}
                  width={24}
                  alt={airdrop?.asset_name}
                />
              )}
            </div>
            <div className="flex flex-col gap-3 w-full">
              <div className="flex items-center gap-2">
                <Typography type="heading" variant="xxsmall">
                  {airdrop?.asset_name}
                </Typography>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex flex-col gap-0.5">
                  <Typography
                    type="misc"
                    variant="helper"
                    className="text-text-secondary"
                  >
                    Release Date
                  </Typography>
                </div>
                <div className="flex flex-col gap-0.5">
                  <Typography
                    type="body"
                    variant="xsmall"
                    className="text-right"
                  >
                    {airdrop?.release_date}
                  </Typography>
                </div>
              </div>
            </div>
          </Card>
        ))}
        {showViewMore && (
          <KButton
            variant="outline"
            size="xsmall"
            onClick={() => setFutureExpanded(true)}
            className="w-fit"
          >
            <Typography
              type="button"
              variant="xsmall"
              className="text-button-secondary-text leading-3"
            >
              View More
            </Typography>
          </KButton>
        )}
      </div>
    );
  };

  const renderClaimedAirdropsTabPanel = () => {
    const showViewMore = claimedAirdrops.length > 3 && !claimedExpanded;
    const visibleAirdrops = claimedExpanded
      ? claimedAirdrops
      : claimedAirdrops.slice(0, 3);

    if (claimedAirdrops.length === 0) {
      return (
        <Box padding="1rem 1rem 1rem 0.5rem">
          <Text>No claimed airdrops available.</Text>
        </Box>
      );
    }

    return (
      <Box>
        <Table>
          <TableHeader>
            <TableRow style={{ borderBottom: "none" }}>
              <TableHead className="pl-1">
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Asset
                </Typography>
              </TableHead>
              <TableHead>
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Claimed amount
                </Typography>
              </TableHead>
              <TableHead>
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  Details
                </Typography>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleAirdrops.map((airdrop, index) => {
              const { data, isLoading: airdropLoading } = airdrop;
              if (airdropLoading) {
                return null;
              }

              return (
                <TableRow key={index}>
                  <TableCell className="pl-1">
                    <Flex gap="0.4rem" alignItems="center">
                      <BaseImage
                        src={airdrop.data.iconPath}
                        height={20}
                        width={20}
                        alt={airdrop.key}
                      />
                      <Typography type="body" variant="xsmall">
                        {data.asset}
                      </Typography>
                    </Flex>
                  </TableCell>
                  <TableCell>
                    <Typography type="body" variant="xsmall">
                      {`${formatEtherNumber(data.claimedAmount.toString())} ${
                        data.unit
                      }`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isKernelAirdropClaim(data.type) ? (
                      <Link
                        href="https://kerneldao.com/stake/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm underline"
                      >
                        View staked $KERNEL
                      </Link>
                    ) : (
                      <Typography type="body" variant="xsmall">
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {showViewMore && (
          <div className="mt-4">
            <KButton
              variant="outline"
              size="xsmall"
              onClick={() => setClaimedExpanded(true)}
            >
              <Typography
                type="button"
                variant="xsmall"
                className="text-button-secondary-text leading-3"
              >
                View More
              </Typography>
            </KButton>
          </div>
        )}
      </Box>
    );
  };

  const renderClaimedAirdropsMobilePanel = () => {
    const showViewMore = claimedAirdrops.length > 3 && !claimedExpanded;
    const visibleAirdrops = claimedExpanded
      ? claimedAirdrops
      : claimedAirdrops.slice(0, 3);

    if (claimedAirdrops.length === 0) {
      return (
        <div className="px-4 py-2">
          <Text>No claimed airdrops available.</Text>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        {visibleAirdrops.map((airdrop, index) => {
          const { data, isLoading: airdropLoading } = airdrop;
          if (airdropLoading) {
            return null;
          }

          return (
            <Card
              key={index}
              variant="non-bordered"
              className="flex gap-3 p-4 rounded-xl"
            >
              <div className="flex items-start">
                <BaseImage
                  src={airdrop.data.iconPath}
                  height={24}
                  width={24}
                  alt={airdrop.key}
                />
              </div>
              <div className="flex flex-col gap-3 w-full">
                <div className="flex items-center gap-2">
                  <Typography type="heading" variant="xxsmall">
                    {data?.asset}
                  </Typography>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex flex-col gap-0.5">
                    <Typography type="body" variant="xsmall">
                      {`${formatEtherNumber(data.claimedAmount.toString())} ${
                        data.unit
                      }`}
                    </Typography>
                    <Typography
                      type="misc"
                      variant="helper"
                      className="text-text-secondary"
                    >
                      Claimed amount
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {isKernelAirdropClaim(data.type) ? (
                      <Link
                        href="https://kerneldao.com/stake/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm underline text-right"
                        onClick={() =>
                          trackEvent(VIEW_STAKED_KERNEL, {
                            location: "active_airdrops",
                          })
                        }
                      >
                        View staked $KERNEL
                      </Link>
                    ) : (
                      <Typography
                        type="body"
                        variant="xsmall"
                        className="text-right"
                      >
                        -
                      </Typography>
                    )}
                    <Typography
                      type="misc"
                      variant="helper"
                      className="text-text-secondary text-right"
                    >
                      Details
                    </Typography>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
        {showViewMore && (
          <KButton
            variant="outline"
            size="xsmall"
            onClick={() => setClaimedExpanded(true)}
            className="w-fit"
          >
            <Typography
              type="button"
              variant="xsmall"
              className="text-button-secondary-text leading-3"
            >
              View More
            </Typography>
          </KButton>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4">
        {!isConnected ? (
          <Disconnected
            onConnectWallet={handleConnectWallet}
            subtitle="Please link your wallet to view airdrops."
          />
        ) : (
          <Card
            variant="non-bordered"
            className="flex flex-col gap-6 h-fit rounded-3xl p-0 lg:p-8 bg-transparent lg:bg-white"
          >
            <Tab
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
            {renderTabs()}
            <GainNudge />
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
};
export default ActiveAirDrops;
